import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useToast} from '../context/ToastContext';
import AuthImage from './AuthImage';
import {HangerIcon, HeartIcon} from './icons';
import {slotForCategoryCode} from './outfit-builder/steps';
import {pluralDays} from '../utils/plural';

interface RediscoverItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    /** Код категории — по нему считается слот конструктора (ключ к «собрать образ с этой вещью»). */
    categoryCode?: string | null;
    categoryId?: string | null;
    primaryPhotoUrl: string | null;
    rating: number | null;
    /** Ценность вещи в шкале 0–100 — то же число, что в рекомендациях и очереди выбытия. */
    itemScore100?: number | null;
    /** Вещь отмечена избранной владельцем. */
    favorite?: boolean;
    daysSinceWorn: number | null;
}

interface Rediscover {
    items: RediscoverItem[];
    consentRequired?: boolean;
}

/**
 * Состояние журнала носки. R06/F09: пустой журнал не означает «давно не носили» — по нему
 * забытую вещь от регулярно носимой без отметок не отличить, и утверждать за пользователя
 * блок не вправе.
 */
const wornLabel = (days: number | null): string =>
    days == null ? 'нет записей о носке' : `последняя отметка носки — ${pluralDays(days)} назад`;

/**
 * Самостоятельная карточка «Вспомнить о любимом»: ценные вещи, которых давно нет в журнале
 * носки. Грузит данные сама; некритичная — при ошибке/пустоте/отзыве согласия не отображается.
 *
 * <p>Основное действие — «Собрать образ с этой вещью»: открывает существующий конструктор с
 * якорной вещью тем же контрактом, что кнопка карточки вещи (`state.prefillItems`). Своей
 * сборки блок не делает. Вторичное — «Уже надел»: отмечает носку сегодняшним днём.</p>
 */
const RediscoverCard: React.FC = () => {
    const {showToast} = useToast();
    const navigate = useNavigate();
    const [items, setItems] = useState<RediscoverItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<Rediscover>('/rediscover');
            if (res.data.consentRequired || !res.data.items || res.data.items.length === 0) {
                setHidden(true);
            } else {
                setItems(res.data.items);
            }
        } catch (e) {
            setHidden(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    /** Отмена носки (QA-8): возвращаем вещь в список — карточка снова предлагает её надеть. */
    const undoWear = async (eventId: string, item: RediscoverItem) => {
        try {
            const api = await createAxiosInstance();
            await api.delete(`/wear-history/${eventId}`);
            setItems(prev => (prev.some(i => i.id === item.id) ? prev : [item, ...prev]));
            showToast('Носка отменена', 'success');
        } catch {
            showToast('Не удалось отменить носку', 'error');
        }
    };

    const wear = async (item: RediscoverItem) => {
        setBusyId(item.id);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<{eventId?: string; alreadyWornToday?: boolean}>(
                `/clothing/items/${item.id}/wear`);
            setItems(prev => prev.filter(i => i.id !== item.id));
            const eventId = res.data?.eventId;
            showToast(
                res.data?.alreadyWornToday ? 'Уже отмечено сегодня' : 'Надел(а) сегодня',
                res.data?.alreadyWornToday ? 'info' : 'success',
                eventId ? {label: 'Отменить', onClick: () => void undoWear(eventId, item)} : undefined,
            );
        } catch (e) {
            // оставляем вещь в списке при сбое
            showToast('Не удалось отметить носку', 'error');
        } finally {
            setBusyId(null);
        }
    };

    /**
     * Переход в конструктор с якорной вещью — тот же контракт, что у карточки вещи
     * (CTHS-315): состав едет в `state.prefillItems`, слот считается по коду категории.
     */
    const buildOutfit = (item: RediscoverItem, slot: string) => {
        navigate('/outfits/builder', {
            state: {
                prefillItems: [{
                    id: item.id,
                    name: item.name,
                    brandName: item.brandName,
                    primaryPhotoUrl: item.primaryPhotoUrl,
                    slot,
                    categoryId: item.categoryId ?? null,
                }],
            },
        });
    };

    if (loading) {
        return (
            <div className="card p-6 animate-pulse" data-testid="rediscover-loading">
                <div className="h-5 w-48 bg-gray-200 dark:bg-ink-700 rounded mb-4"/>
                <div className="h-16 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </div>
        );
    }

    if (hidden || items.length === 0) {
        return null;
    }

    return (
        <div className="card p-6 space-y-4" data-testid="rediscover">
            <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Вспомнить о любимом</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Ценные вещи, которых давно нет в журнале носки.
                </p>
            </div>
            <ul className="space-y-3">
                {items.map(item => {
                    const slot = slotForCategoryCode(item.categoryCode);
                    const subtitle = [item.brandName, item.categoryName].filter(Boolean).join(' · ');
                    return (
                        <li key={item.id}
                            data-testid={`rediscover-item-${item.id}`}
                            className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border
                                border-gray-200 dark:border-ink-700 p-3">
                            <Link
                                to={`/clothing/${item.id}`}
                                tabIndex={-1}
                                aria-hidden="true"
                                className="shrink-0"
                            >
                                <AuthImage
                                    imageKey={item.primaryPhotoUrl ?? ''}
                                    alt={item.name}
                                    variant="thumbnail"
                                    className="w-20 h-20 rounded-lg object-cover bg-gray-100 dark:bg-ink-700"
                                />
                            </Link>

                            <div className="min-w-0 flex-1">
                                <Link
                                    to={`/clothing/${item.id}`}
                                    title={item.name}
                                    data-testid={`rediscover-link-${item.id}`}
                                    className="block truncate font-medium text-gray-900 dark:text-gray-100
                                        hover:text-brand-600 dark:hover:text-brand-400 hover:underline"
                                >
                                    {item.name}
                                </Link>
                                {subtitle && (
                                    <div className="truncate text-sm text-gray-500 dark:text-gray-400"
                                        title={subtitle}>
                                        {subtitle}
                                    </div>
                                )}
                                {/* Причина показа — только факты: ценность, избранное, состояние журнала. */}
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5"
                                    data-testid={`rediscover-reason-${item.id}`}>
                                    {item.itemScore100 != null && (
                                        <span className="chip">Ценность {item.itemScore100} из 100</span>
                                    )}
                                    {item.favorite && (
                                        <span className="chip">
                                            <HeartIcon className="w-3 h-3 mr-1" filled/>В избранном
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                        {wornLabel(item.daysSinceWorn)}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 sm:shrink-0">
                                {slot && (
                                    <button
                                        type="button"
                                        onClick={() => buildOutfit(item, slot)}
                                        data-testid={`rediscover-build-${item.id}`}
                                        className="min-h-[44px] inline-flex items-center gap-2 px-4 rounded-xl
                                            text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700
                                            pressable focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                                    >
                                        <HangerIcon className="w-4 h-4"/>
                                        Собрать образ с этой вещью
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => wear(item)}
                                    disabled={busyId === item.id}
                                    title="Отметить носку сегодняшним днём"
                                    data-testid={`rediscover-wear-${item.id}`}
                                    className="btn-secondary min-h-[44px]"
                                >
                                    Уже надел
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default RediscoverCard;
